import React, { useEffect, useState, useMemo } from 'react';
import { MdLockClock } from 'react-icons/md';
import { capSuspendedView, notifyCapCleared, notifyDailyLimitReached } from 'utils/daily-limit-event.util';

// Listens for the `cap-suspended` window event dispatched by axiosClient
// when BE returns 403 + code=CAP_SUSPENDED. Renders a friendly overlay
// with a live countdown to 00:01 VN (when the reset cron clears
// capSuspendedAt) so HV understands this is a daily rule, not a bug.
//
// Mounts once at the App root — every page sharing axiosClient benefits
// without per-page wiring. Components that need their own behavior
// (e.g. pause auto-save in ElearningStudentBookPage) keep listening to
// the same event independently; this overlay coexists.

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.78)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 99998, // below SessionKickedOverlay (99999) — session-kick wins
  padding: '16px',
};

const cardStyle = {
  backgroundColor: '#fff', borderRadius: '14px',
  padding: '36px 28px', maxWidth: '460px', width: '100%',
  textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
};

const iconWrapStyle = {
  width: '88px', height: '88px', borderRadius: '50%',
  background: 'linear-gradient(135deg, #fff7e6 0%, #ffe0b2 100%)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  marginBottom: '20px',
};

const titleStyle = {
  fontSize: '20px', fontWeight: 700, color: '#d97706',
  marginBottom: '10px',
};

const messageStyle = {
  fontSize: '14px', color: '#4b5563', lineHeight: 1.6, marginBottom: '20px',
};

const countdownBoxStyle = {
  background: '#f9fafb', borderRadius: '10px', padding: '14px 16px',
  marginBottom: '20px', border: '1px solid #e5e7eb',
};

const countdownLabelStyle = {
  fontSize: '12px', color: '#6b7280', textTransform: 'uppercase',
  letterSpacing: '0.5px', marginBottom: '6px',
};

const countdownValueStyle = {
  fontSize: '22px', fontWeight: 700, color: '#111827',
  fontVariantNumeric: 'tabular-nums',
};

const btnStyle = {
  background: '#2563eb', color: '#fff', border: 'none',
  borderRadius: '8px', padding: '11px 22px', fontSize: '14px',
  fontWeight: 600, cursor: 'pointer',
};

const secondaryBtnStyle = {
  background: '#fff', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: '8px', padding: '11px 22px', fontSize: '14px',
  fontWeight: 600, cursor: 'pointer', marginRight: '10px',
};

// Compute ms until next VN midnight + 1 minute (when reset-cron fires).
// Caller may pass resetAt ISO from BE; falls back to client-side compute.
const computeMsToReset = (resetAtIso) => {
  if (resetAtIso) {
    const target = new Date(resetAtIso).getTime();
    if (Number.isFinite(target)) return Math.max(0, target - Date.now());
  }
  const UTC7_OFFSET_MS = 7 * 60 * 60 * 1000;
  const nowVn = new Date(Date.now() + UTC7_OFFSET_MS);
  const tomorrowVn = new Date(nowVn);
  tomorrowVn.setUTCHours(24, 1, 0, 0);
  return Math.max(0, tomorrowVn.getTime() - nowVn.getTime());
};

const formatHMS = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export default function CapSuspendedOverlay() {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const onCapSuspended = (e) => {
      // Flag global so axiosClient's backup alert (200ms delay) skips —
      // this component is now handling the UX.
      window.__capSuspendedHandled = true;
      setDetail(e?.detail || {});
      setVisible(true);
    };
    window.addEventListener('cap-suspended', onCapSuspended);
    return () => window.removeEventListener('cap-suspended', onCapSuspended);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [visible]);

  const remainingMs = useMemo(
    () => computeMsToReset(detail?.resetAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail?.resetAt, now],
  );

  // Auto-dismiss once countdown reaches zero — the next BE call will
  // succeed (cron has cleared capSuspendedAt), so re-showing the overlay
  // is unnecessary. If somehow it's still blocked, the next 403 re-fires
  // the event and re-opens this overlay.
  const view = capSuspendedView(detail);
  useEffect(() => {
    if (visible && view.autoDismiss && remainingMs <= 0) {
      setVisible(false);
      window.__capSuspendedHandled = false;
      // Sang ngày mới → layout hỏi lại giờ để gỡ trạng thái chặn, trang sách ghi giờ lại.
      notifyDailyLimitReached();
      notifyCapCleared();
    }
  }, [visible, view.autoDismiss, remainingMs]);

  if (!visible) return null;

  const goHome = () => {
    window.__capSuspendedHandled = false;
    window.location.href = '/';
  };

  const dismiss = () => {
    setVisible(false);
    window.__capSuspendedHandled = false;
    notifyCapCleared();
  };

  return (
    <div style={overlayStyle} role='dialog' aria-modal='true' aria-labelledby='cap-suspended-title'>
      <div style={cardStyle}>
        <div style={iconWrapStyle}>
          <MdLockClock size={44} color='#d97706' />
        </div>
        <div id='cap-suspended-title' style={titleStyle}>
          {view.title}
        </div>
        <div style={messageStyle}>
          {detail?.message
            || 'Bạn đã học đủ thời gian quy định cho hôm nay. Hệ thống tạm khoá để đảm bảo hiệu quả học tập theo quy định.'}
        </div>
        {view.showCountdown && (
          <div style={countdownBoxStyle}>
            <div style={countdownLabelStyle}>Mở lại sau</div>
            <div style={countdownValueStyle}>{formatHMS(remainingMs)}</div>
          </div>
        )}
        <div>
          <button type='button' style={secondaryBtnStyle} onClick={dismiss}>
            Để sau
          </button>
          <button type='button' style={btnStyle} onClick={goHome}>
            Về trang chủ
          </button>
        </div>
      </div>
    </div>
  );
}
